/**
 * The geometry of the script strip (ScriptTimeline): how wide the lanes are, and which cells have
 * room for a label. The strip never squeezes a long song into the screen: it has a floor of pixels
 * per second, and it scrolls sideways past the screen width. Pure.
 */

/** The zoom steps of the strip. 1 = the song fits the screen, or the floor when it is longer. */
export const STRIP_ZOOMS = [1, 2, 4] as const;
export type StripZoom = (typeof STRIP_ZOOMS)[number];

/** The fewest pixels for one second of the song at zoom 1. A 4-minute song is about 1,900 px. */
export const STRIP_MIN_PX_PER_SEC = 8;

/** The width the strip uses when the screen width is not known yet (a test, the first paint). */
export const STRIP_FALLBACK_PX = 960;

/** The gap between two cells on one lane. */
export const STRIP_CELL_GAP_PX = 2;

/** The width of one character of the 11 px mono labels, with a little room. */
const CHAR_PX = 7;
/** The inner padding of a labelled cell, both sides together. */
const CELL_PAD_PX = 10;

/** The width of the lanes in pixels for a screen width, a song length and a zoom step. */
export function stripTrackWidth(viewportPx: number, durationSec: number, zoom: StripZoom): number {
  const viewport = viewportPx > 0 ? viewportPx : STRIP_FALLBACK_PX;
  const floor = Math.max(1, durationSec) * STRIP_MIN_PX_PER_SEC;
  return Math.round(Math.max(viewport, floor) * zoom);
}

/** The place of a cell on a lane in pixels: the left edge and the width (less the gap, at least 1 px). */
export function stripCell(
  startSec: number,
  endSec: number,
  durationSec: number,
  trackPx: number
): { left: number; width: number } {
  const span = Math.max(1, durationSec);
  const at = (sec: number) => (Math.max(0, Math.min(span, sec)) / span) * trackPx;
  const left = at(startSec);
  return { left, width: Math.max(1, at(endSec) - left - STRIP_CELL_GAP_PX) };
}

/** True when a text of `chars` characters fits in a cell of `widthPx`. */
export function labelFits(chars: number, widthPx: number): boolean {
  return chars > 0 && widthPx >= chars * CHAR_PX + CELL_PAD_PX;
}

/** The label of a shot cell: its number when there is room, else nothing (the tooltip says it). */
export function shotCellLabel(number: number, widthPx: number): string {
  const text = String(number);
  return labelFits(text.length, widthPx) ? text : '';
}

/**
 * The label of a section cell: the full name ("Chorus 1") when there is room, else the short name
 * ("C1"), else nothing (the tooltip says it).
 */
export function sectionCellLabel(fullName: string, shortName: string, widthPx: number): string {
  if (labelFits(fullName.length, widthPx)) {
    return fullName;
  }
  return labelFits(shortName.length, widthPx) ? shortName : '';
}

/** The fewest pixels of an effect mark, so a short transition stays visible. */
export const STRIP_MIN_EFFECT_PX = 8;

/** The place of an effect mark: at least `STRIP_MIN_EFFECT_PX` wide, centred on its time. */
export function stripEffectMark(
  startSec: number,
  endSec: number,
  durationSec: number,
  trackPx: number
): { left: number; width: number } {
  const cell = stripCell(startSec, endSec, durationSec, trackPx);
  if (cell.width >= STRIP_MIN_EFFECT_PX) {
    return cell;
  }
  const centre = cell.left + cell.width / 2;
  const left = Math.max(0, Math.min(trackPx - STRIP_MIN_EFFECT_PX, centre - STRIP_MIN_EFFECT_PX / 2));
  return { left, width: STRIP_MIN_EFFECT_PX };
}
